import { FormProvider, useForm } from "react-hook-form";
import { Grid, Typography } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { TRADE_SIDE, TRADE_TYPE } from "pages/BasicTrade/components/constants";
import { useParams, useSearchParams } from "react-router-dom";

import AdvanceTradeFormSkeleton from "components/Skeletons/AdvanceTradeForm.Skeleton";
import { GetTradeAmountMax } from "store/api/trade";
import GoldTotal from "./GoldTotal";
import NumberFormats from "components/NumberFormats";
import { SpaceBetweenBox } from "components/GeneralStyled";
import SubmitTrade from "./SubmitTrade";
import TradeInput from "./TradeInput";
import TradeRange from "./TradeRange";
import TradeTotal from "./TradeTotal";
import http from "config/http";
import { repeat } from "utils/helpers/interval";
import { useSelector } from "react-redux";
import { useState } from "react";

function AdvancedTradeInputs({ sideId, handleGetWalletInfo }) {
  const tradeInitial = useSelector((state) => state.trade.tradeInitial?.data);
  const coinMarketDetails = useSelector(
    (state) => state.market.singleMarketLoggedInList
  );

  const [tradeAmountMax, setTradeAmountMax] = useState();

  const [searchParams] = useSearchParams();
  const walletTypeId = +searchParams.get("walletTypeId") || 1;
  const amountTypeId = +searchParams.get("amountTypeId") || 1;
  const tradeTypeId = +searchParams.get("tradeTypeId") || 1;
  const borrow = +searchParams.get("borrow");
  const repay = +searchParams.get("repay");
  const transfer = +searchParams.get("transfer");

  const { source, destination } = useParams();

  const defaultValues = {
    coinId: +source,
    coinMasterId: +destination,
    sideId,
    walletTypeId,
    amountTypeId,
    tradeTypeId,
    amount: "",
    price: "",
  };

  const methods = useForm({ defaultValues });
  const { watch, reset } = methods;
  const coinId = watch("coinId");
  const coinMasterId = watch("coinMasterId");

  const getTradeAmountMax = async () => {
    try {
      const { data } = await http.get(
        GetTradeAmountMax({
          coinId: +source,
          coinMasterId: +destination,
          sideId,
          walletTypeId,
        })
      );

      setTradeAmountMax(data?.data);
    } catch (e) {}
  };

  const isPrice = useMemo(() => {
    const selectedTradeType = tradeInitial?.lisTradeType?.find(
      (tradeType) => tradeType?.id === tradeTypeId
    );

    return selectedTradeType?.isPrice;
  }, [tradeInitial?.lisTradeType, tradeTypeId]);

  const coinPairSetting = useMemo(
    () =>
      tradeInitial?.lisCoinPairSetting?.find(
        (setting) =>
          setting?.coinSourceId === coinId &&
          setting?.coinDestinationId === coinMasterId
      ),
    [tradeInitial?.lisCoinPairSetting, coinId, coinMasterId]
  );

  const srcDecimalCount = coinPairSetting?.decimalPlacesCoinSource;
  const destDecimalCount = coinPairSetting?.decimalPlacesCoinDestination;

  const sourceCoin = useMemo(() => {
    return tradeInitial?.lisCoin?.find((coin) => coin?.id === +source);
  }, [source, tradeInitial?.lisCoin]);

  const destinationCoin = useMemo(() => {
    return tradeInitial?.lisCoin?.find((coin) => coin?.id === +destination);
  }, [destination, tradeInitial?.lisCoin]);

  useEffect(() => {
    let getTradeAmountMaxInterval = null;

    if (tradeInitial?.validSeconds) {
      getTradeAmountMax();

      clearInterval(getTradeAmountMaxInterval);

      getTradeAmountMaxInterval = repeat({
        action: () => getTradeAmountMax(),
        time: tradeInitial?.validSeconds * 1000,
      });
    }

    return () => {
      clearInterval(getTradeAmountMaxInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tradeInitial?.validSeconds,
    walletTypeId,
    destination,
    source,
    sideId,
    borrow,
    repay,
    transfer,
  ]);

  useEffect(() => {
    reset({
      coinId: +source,
      coinMasterId: +destination,
      sideId,
      walletTypeId,
      amountTypeId,
      tradeTypeId,
      amount: "",
      price: "",
    });
  }, [
    tradeTypeId,
    reset,
    amountTypeId,
    source,
    destination,
    sideId,
    walletTypeId,
  ]);

  return (
    <>
      {tradeAmountMax ? (
        <FormProvider {...methods}>
          <form>
            <Grid container>
              <Grid item xs={12}>
                <SpaceBetweenBox mb={0}>
                  <Typography color="text.secondary" variant="h6">
                    Available:
                  </Typography>

                  <Typography color="text.main" variant="h6">
                    <NumberFormats
                      value={tradeAmountMax?.availableBalance || 0}
                    />{" "}
                    <Typography
                      component="small"
                      variant="subtitle1"
                      color="text.secondary"
                    >
                      {sideId === TRADE_SIDE.Sell.id
                        ? sourceCoin?.name
                        : destinationCoin?.name}
                    </Typography>
                  </Typography>
                </SpaceBetweenBox>
              </Grid>

              <TradeInput
                coinPairSetting={coinPairSetting}
                tradeAmountMax={tradeAmountMax}
                srcDecimalCount={srcDecimalCount}
                destDecimalCount={destDecimalCount}
                coinMarketDetails={coinMarketDetails}
                isPrice={isPrice}
              />

              <TradeRange
                tradeAmountMax={tradeAmountMax}
                srcDecimalCount={srcDecimalCount}
                destDecimalCount={destDecimalCount}
              />

              <GoldTotal
                tradeAmountMax={tradeAmountMax}
                sourceCoin={sourceCoin}
              />

              {tradeTypeId !== TRADE_TYPE.market.id && (
                <TradeTotal
                  tradeAmountMax={tradeAmountMax}
                  srcDecimalCount={srcDecimalCount}
                  destDecimalCount={destDecimalCount}
                />
              )}

              <SubmitTrade
                tradeAmountMax={tradeAmountMax}
                destinationCoin={destinationCoin}
                isPrice={isPrice}
                sideId={sideId}
                getTradeAmountMax={getTradeAmountMax}
                handleGetWalletInfo={handleGetWalletInfo}
              />
            </Grid>
          </form>
        </FormProvider>
      ) : (
        <AdvanceTradeFormSkeleton />
      )}
    </>
  );
}

export default AdvancedTradeInputs;
